import type { ThemeDefinition } from 'vuetify'

export const staticPrimaryColor = '#F27063'
export const staticPrimaryDarkenColor = '#D85F4F'

// MAIN THEME COLORS
// #F26F63 - Primary (Soft Red)
// #D99441 - Secondary (Warm Orange)
// #77882B - Success (Olive Green)
// #8FB5D9 - Info (Soft Blue)
// #D89441 - Warning (Golden Orange)
// #F27063 - Error (Soft Red)

export const themes: Record<string, ThemeDefinition> = {
  light: {
    dark: false,
    colors: {
      'primary': '#F26F63',
      'on-primary': '#fff',
      'primary-darken-1': '#D85F4F',
      'primary-light': '#F58B7D',
      'secondary': '#D99441',
      'on-secondary': '#fff',
      'secondary-darken-1': '#7A9EC4',
      'secondary-light': '#A5C5E3',
      'success': '#77882B',
      'on-success': '#fff',
      'success-darken-1': '#687726',
      'success-light': '#8B9C3F',
      'info': '#8FB5D9',
      'on-info': '#fff',
      'info-darken-1': '#7A9EC4',
      'info-light': '#A5C5E3',
      'warning': '#D89441',
      'on-warning': '#fff',
      'warning-darken-1': '#C28439',
      'warning-light': '#E0A75B',
      'error': '#F27063',
      'on-error': '#fff',
      'error-darken-1': '#D85F4F',
      'error-light': '#F58B7D',
      'background': '#F4ECD7',
      'on-background': '#22303E',
      'surface': '#FFFBF3',
      'on-surface': '#22303E',
      'grey-50': '#FAFAFA',
      'grey-100': '#F5F5F5',
      'grey-200': '#EEEEEE',
      'grey-300': '#E0E0E0',
      'grey-400': '#BDBDBD',
      'grey-500': '#9E9E9E',
      'grey-600': '#757575',
      'grey-700': '#616161',
      'grey-800': '#424242',
      'grey-900': '#212121',
      'grey-light': '#FAFAFA',
      'perfect-scrollbar-thumb': '#DBDADE',
      'skin-bordered-background': '#FFFBF3',
      'skin-bordered-surface': '#FFFBF3',
      'expansion-panel-text-custom-bg': '#F4ECD7',
    },

    variables: {
      'code-color': '#D89441',
      'overlay-scrim-background': '#22303E',
      'tooltip-background': '#22303E',
      'overlay-scrim-opacity': 0.5,
      'hover-opacity': 0.06,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'dragged-opacity': 0.1,
      'disabled-opacity': 0.4,
      'border-color': '#22303E',
      'border-opacity': 0.12,
      'table-header-color': '#FFFBF3',
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,
      'switch-opacity': 0.2,
      'switch-disabled-track-opacity': 0.3,
      'switch-disabled-thumb-opacity': 0.4,
      'switch-checked-disabled-opacity': 0.3,
      'track-bg': '#EEF1F3',
      'chat-bg': '#F7F8F8',

      // Shadows
      'shadow-key-umbra-color': '#22303E',
      'shadow-xs-opacity': 0.06,
      'shadow-sm-opacity': 0.08,
      'shadow-md-opacity': 0.10,
      'shadow-lg-opacity': 0.14,
      'shadow-xl-opacity': 0.18,
    },
  },
  dark: {
    dark: true,
    colors: {
      'primary': '#F27063',
      'on-primary': '#fff',
      'primary-darken-1': '#D85F4F',
      'primary-light': '#F58B7D',
      'secondary': '#8FB5D9',
      'on-secondary': '#fff',
      'secondary-darken-1': '#7A9EC4',
      'secondary-light': '#A5C5E3',
      'success': '#77882B',
      'on-success': '#fff',
      'success-darken-1': '#687726',
      'success-light': '#8B9C3F',
      'info': '#8FB5D9',
      'on-info': '#fff',
      'info-darken-1': '#7A9EC4',
      'info-light': '#A5C5E3',
      'warning': '#D89441',
      'on-warning': '#fff',
      'warning-darken-1': '#C28439',
      'warning-light': '#E0A75B',
      'error': '#F27063',
      'on-error': '#fff',
      'error-darken-1': '#D85F4F',
      'error-light': '#F58B7D',
      'background': '#232333',
      'on-background': '#E6E6F1',
      'surface': '#2B2C40',
      'on-surface': '#E6E6F1',
      'grey-50': '#26293A',
      'grey-100': '#2F3349',
      'grey-200': '#26293A',
      'grey-300': '#4A5072',
      'grey-400': '#5E6692',
      'grey-500': '#7983BB',
      'grey-600': '#AAB3DE',
      'grey-700': '#B6BEE3',
      'grey-800': '#CFD3EC',
      'grey-900': '#E7E9F6',
      'grey-light': '#313246',
      'perfect-scrollbar-thumb': '#4A5072',
      'skin-bordered-background': '#2B2C40',
      'skin-bordered-surface': '#2B2C40',
    },
    variables: {
      'code-color': '#D89441',
      'overlay-scrim-background': '#1D1D2A',
      'tooltip-background': '#E6E6F1',
      'overlay-scrim-opacity': 0.6,
      'hover-opacity': 0.06,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'dragged-opacity': 0.1,
      'disabled-opacity': 0.4,
      'border-color': '#E6E6F1',
      'border-opacity': 0.12,
      'table-header-color': '#2B2C40',
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,
      'switch-opacity': 0.4,
      'switch-disabled-track-opacity': 0.4,
      'switch-disabled-thumb-opacity': 0.8,
      'switch-checked-disabled-opacity': 0.3,
      'track-bg': '#41415F',
      'chat-bg': '#20202E',

      // Shadows
      'shadow-key-umbra-color': '#14141D',
      'shadow-xs-opacity': 0.18,
      'shadow-sm-opacity': 0.20,
      'shadow-md-opacity': 0.22,
      'shadow-lg-opacity': 0.24,
      'shadow-xl-opacity': 0.26,
    },
  },
}

export default themes
